import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { DTOInviteFromSwipeAzarCard } from 'src/apis/dto/DTOInviteFromSwipeAzar';
import { QUALITY_USER_BADGE_STATUS_TYPES } from 'src/apis/dto/DTOQualityUserBadge';
import IcCheckCircleFill from 'src/assets/images/icCheckCircleFill.svg';
import IcGenderFemaleRegular from 'src/assets/images/icGenderFemaleRegular.svg';
import IcGenderMaleRegular from 'src/assets/images/icGenderMaleRegular.svg';
import IcLanguageFill from 'src/assets/images/icLanguageFill.svg';
import IcMoreVerticalRegular from 'src/assets/images/icMoreVerticalRegular.svg';
import IcRadioRegular from 'src/assets/images/icRadioRegular.svg';
import Menu from 'src/components/Menu';
import ProfileImageBase from 'src/components/ProfileImage';
import ProfileModal from 'src/components/ProfileModal/Full';
import QualityUserBadgeBase from 'src/components/QualityUserBadge';
import ReportAndBlockPopup from 'src/components/ReportAndBlockPopup';
import ResizingText from 'src/components/ResizingText';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import { hoverAndActiveCss } from 'src/utils/styled';
import InviteUserCardSwipeWrapper from './InviteUserCardSwipeWrapper';
import InviteUserMoreMenu from './InviteUserMoreMenu';
interface Props {
  user: DTOInviteFromSwipeAzarCard;
  isSelected: boolean;
  onToggleSelect: (cardId: string) => void;
  /** 스와이프로 카드 제거 시 호출 */
  onRemove: (cardId: string) => void;
}

/** 호버 시 배경 + MoreButton 노출, 인터랙션은 하위 버튼으로 위임 */
const Root = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  border-radius: 12px;
  position: relative;
  padding: 4px 24px;

  ${({
  theme
}) => hoverAndActiveCss(`
      background-color: ${theme.color.surface3};
      border-radius: 0;

      .more-button {
        opacity: 1;
        pointer-events: auto;
      }
    `)}
`;

/** 좌측: 프로필 이미지 + 유저 정보 → 탭 시 ProfileModal */
const ProfileArea = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;
const ProfileImageWrap = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 16px;
  overflow: hidden;
  background-color: ${({
  theme
}) => theme.color.surface4};
`;
const ProfileImage = styled(ProfileImageBase)`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;
const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const Name = styled(ResizingText)`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content12};
  min-width: 0;
`;
const Age = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const NameAge = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  min-width: 0;
  overflow: hidden;
`;
const CountryFlag = styled.img`
  width: 14px;
  height: 10px;
  object-fit: cover;
  flex-shrink: 0;
`;
const StyledQualityUserBadge = styled(QualityUserBadgeBase)`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;
const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;
const MetaIcon = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
`;
const metaTextBase = `
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
`;

/** 모바일 max-width: 72px / 데스크탑 max-width: 100px */
const GenderMetaText = styled(ResizingText)`
  ${metaTextBase}
  color: ${({
  theme
}) => theme.color.content9};
  max-width: 100px;
  ${({
  theme
}) => theme.screenSize.mobile} {
    max-width: 72px;
  }
`;

/** 모바일 max-width: 56px / 데스크탑 max-width: 100px */
const LanguageMetaText = styled(ResizingText)`
  ${metaTextBase}
  color: ${({
  theme
}) => theme.color.content9};
  max-width: 100px;
  ${({
  theme
}) => theme.screenSize.mobile} {
    max-width: 56px;
  }
`;
const Dot = styled.span`
  font-size: 13px;
  line-height: 17px;
  font-weight: 400;
  color: ${({
  theme
}) => theme.color.content9};
  flex-shrink: 0;
`;
const MoreButton = styled.button`
  all: unset;
  cursor: pointer;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({
  theme
}) => theme.color.surface5};
  border-radius: 50%;
  background-color: ${({
  theme
}) => theme.color.surface4};
  color: ${({
  theme
}) => theme.color.content9};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;

  svg {
    width: 24px;
    height: 24px;
    color: ${({
  theme
}) => theme.color.contentDeep};
  }
`;

/** 우측: 체크 아이콘 → 탭 시 선택 토글 */
const RadioButton = styled.button`
  all: unset;
  cursor: pointer;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
`;
const RadioFillIcon = styled(IcCheckCircleFill)`
  width: 24px;
  height: 24px;
  color: ${({
  theme
}) => theme.color.primary1};
`;
const RadioOffIcon = styled(IcRadioRegular)`
  width: 24px;
  height: 24px;
`;
function GenderIcon({
  gender
}: {
  gender?: string;
}) {
  if (!gender) return null;
  const Icon = gender === 'FEMALE' ? IcGenderFemaleRegular : IcGenderMaleRegular;
  return <MetaItem data-sentry-element="MetaItem" data-sentry-component="GenderIcon" data-sentry-source-file="InviteUserCard.tsx">
      <MetaIcon data-sentry-element="MetaIcon" data-sentry-source-file="InviteUserCard.tsx">
        <Icon data-sentry-element="Icon" data-sentry-source-file="InviteUserCard.tsx" />
      </MetaIcon>
      <GenderMetaText minFontSize={10} maxLines={1} data-sentry-element="GenderMetaText" data-sentry-source-file="InviteUserCard.tsx">
        {gender === 'FEMALE' ? 'Female' : 'Male'}
      </GenderMetaText>
    </MetaItem>;
}
export default function InviteUserCard({
  user,
  isSelected,
  onToggleSelect,
  onRemove
}: Props) {
  const showModal = useSetAtom(showModalAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const device = useCssMediaDevice();
  const {
    cardId,
    profileCardType: cardType
  } = user;
  const profileImageUrl = user.profileMediaInfos[0]?.thumbnailImageUrl;
  const countryCode = user.location?.countryCode;
  const language = user.preferredLanguages[0]?.displayName;
  const visibleProfileImageOption = user.profileMediaInfos.length > 0;
  const handleRemove = () => {
    onRemove(cardId);
  };
  const handleRemoveFromProfileModal = () => {
    closeModal(ModalType.PROFILE_MODAL);
    handleRemove();
  };
  const handleProfileClick = () => {
    showModal({
      key: ModalType.PROFILE_MODAL,
      component: () => <ProfileModal type={cardType} id={cardId} eventPage='swipe_invite' eventSection='none' eventTab='mirror' onReportSuccess={handleRemoveFromProfileModal} />
    });
  };
  const handleReport = () => {
    const eventParams = {
      tab: device === 'desktop' ? 'mirror' : 'anywhere',
      page: device === 'desktop' ? 'swipe_invite' : 'profile',
      section: 'none',
      target: device === 'desktop' ? 'text' : 'list',
      target_id: 'card_id',
      target_detail: cardId
    };
    pushEvent({
      eventType: EVENT_TYPE.REPORT,
      eventName: EVENT_NAME.REPORT_BLOCK_CLICK,
      eventParams: {
        ...eventParams,
        action_category: device === 'desktop' ? 'click' : 'swipe'
      }
    });
    showModal({
      key: ModalType.REPORT_AND_BLOCK_POPUP,
      component: () => <ReportAndBlockPopup cardId={cardId} cardType={cardType} visibleProfileImageOption={visibleProfileImageOption} onSuccess={handleRemove} eventParams={eventParams} />
    });
  };
  const card = <Root data-testid='InviteUserCard'>
      <ProfileArea onClick={handleProfileClick} data-testid='InviteUserCard-profile'>
        <ProfileImageWrap>
          <ProfileImage src={profileImageUrl} alt={user.nickname} />
        </ProfileImageWrap>
        <UserInfo>
          <NameRow>
            <NameAge>
              <Name minFontSize={12} maxLines={2}>
                {user.nickname}
              </Name>
              {user.age != null && <Age>{`, ${user.age}`}</Age>}
            </NameAge>
            {user.qualityUserBadge?.state === QUALITY_USER_BADGE_STATUS_TYPES.ACHIEVED && <StyledQualityUserBadge qualityUserBadge={user.qualityUserBadge} isMine={false} showModalOnClick={false} />}
            {countryCode && <CountryFlag src={`/images/flags/icFlag${countryCode}.png`} alt={`${countryCode} flag`} />}
          </NameRow>
          <MetaRow>
            <GenderIcon gender={user.gender} />
            {user.gender && language && <Dot>∙</Dot>}
            {language && <MetaItem>
                <MetaIcon>
                  <IcLanguageFill />
                </MetaIcon>
                <LanguageMetaText minFontSize={10} maxLines={1}>
                  {language}
                </LanguageMetaText>
              </MetaItem>}
          </MetaRow>
        </UserInfo>
      </ProfileArea>

      {/* 데스크탑만 노출 모바일은 스와이프 */}
      {device === 'desktop' && <Menu renderButton={onClickButton => <MoreButton className='more-button' onClick={e => {
      e.stopPropagation();
      onClickButton();
    }} data-testid='InviteUserCard-more'>
              <IcMoreVerticalRegular />
            </MoreButton>} align='end'>
          <InviteUserMoreMenu onReport={handleReport} onDelete={handleRemove} />
        </Menu>}

      <RadioButton onClick={() => onToggleSelect(cardId)} aria-label={isSelected ? '선택 해제' : '선택'} aria-pressed={isSelected} data-testid='InviteUserCard-radio'>
        {isSelected ? <RadioFillIcon /> : <RadioOffIcon />}
      </RadioButton>
    </Root>;
  if (device !== 'desktop') {
    return <InviteUserCardSwipeWrapper onSwipeRight={handleReport} onSwipeLeft={handleRemove}>
        {card}
      </InviteUserCardSwipeWrapper>;
  }
  return card;
}